import React, {FC, useEffect, useState} from "react";
import {CardMedia, IconButton, Typography} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import axios from "axios";
import clsx from "clsx";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import DeleteIcon from "@material-ui/icons/Delete";
import FfairTooltip from "view/components/Layout/Tooltip";
import {IFile} from "@evennt-tech/api-v2-core";
import useStyles from "./styles";

type Props = {
  file: IFile | RawAttachmentFile;
  handleRemoveFile: () => void;
  classes?: {
    fileName?: string;
  };
};

export async function getFileType(url: string) {
  try {
    const response = await axios.get(url, {responseType: "blob"});
    return response.data.type;
  } catch (error) {
    console.error("Error fetching file type:", error);
    return null;
  }
}

const TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
};

// A saved file's URL redirects to presigned storage that sends no CORS
// headers, so fetching it from the page fails. The local File or the
// extension is enough to tell an image from a PDF.
export function getLocalFileType(file: IFile | RawAttachmentFile) {
  if ("file" in file && file.file?.type) {
    return file.file.type;
  }
  const extension = file.title?.split(".").pop()?.toLowerCase() ?? "";
  return TYPES_BY_EXTENSION[extension] ?? null;
}

const FilePreview: FC<Props> = ({
  file,
  handleRemoveFile,
  classes: propsClasses,
}) => {
  const classes = useStyles();
  const [fileType, setFileType] = useState<string | null>(null);
  const localType = getLocalFileType(file);

  useEffect(() => {
    if (localType) {
      setFileType(localType);
      return;
    }

    async function fetchFileType() {
      if (file?.url) {
        const type = await getFileType(file.url);
        setFileType(type);
      }
    }

    fetchFileType();
  }, [file.url, localType]);

  return (
    <FlexBox alignCenter className={classes.filePreview}>
      <FlexBox alignCenter>
        {fileType !== "application/pdf" ? (
          <CardMedia
            component="img"
            image={file.url}
            alt="Signature preview"
            className={classes.fileImage}
          />
        ) : (
          <PictureAsPdfIcon className={classes.fileImage} />
        )}
        <FfairTooltip title={file.title}>
          <Typography
            className={clsx(propsClasses?.fileName, classes.fileName)}
          >
            {file.title}
          </Typography>
        </FfairTooltip>
      </FlexBox>
      <IconButton onClick={handleRemoveFile}>
        <DeleteIcon />
      </IconButton>
    </FlexBox>
  );
};

export default FilePreview;
